/**
 * CheckersGame — authoritative rules engine (RULES.md v1, 2026-10-06 revision).
 * Pure logic, zero DOM/Three dependencies: usable by client, AI and server.
 *
 * Turn model (RULES.md §4):
 *  - One step OR one hop chain per turn — a turn is a single atomic action.
 *  - Chain hops are always available but continuation is OPTIONAL: any landing
 *    along any reachable hop path is a legal final destination (§4.2). The
 *    caller picks a destination from `legalTargets()` and the engine executes
 *    the whole path in one `applyMove`, then the turn rotates.
 *  - No-revisit: a hop path may never land on a cell it already occupied (§4.4);
 *    enforced inside the BFS enumeration, so no persistent chain state exists.
 *  - Must-move: a player with any legal move must take it; a player with none
 *    is skipped by the caller (see `hasAnyMove` / `skipTurn`).
 */
import type { HexPos } from '../types/game.js';
import type { MatchState } from '../net/messages.js';
import { HEX_DIRECTIONS, hexKey, hexDistance, isOnBoard, parseHexKey } from '../utils/hexMath.js';
import {
  CAMP_POSITIONS,
  SEAT_LAYOUTS,
  destinationCamp,
  campOf,
  teamOfSeatIndex,
  teamSeatIndices,
  type PlayerCount,
  type TeamId,
} from '../constants/camps.js';

export const MARBLES_PER_CAMP = 10;

export interface Target {
  to: HexPos;
  type: 'step' | 'hop';
  /**
   * Full path from the moving marble's origin to `to`, both ends inclusive.
   * Steps are always `[from, to]`; hops list every landing of the chain.
   */
  path: HexPos[];
}

export interface SeatTargets {
  seat: number;
  marble: HexPos;
  targets: Target[];
}

export interface MoveResult {
  /** Path the marble travelled (origin → destination, every landing). */
  path: HexPos[];
  finished: boolean;
  winner: number | null;
  /**
   * Winning team in team formats (RULES.md §11: 4P = 2v2, 6P = 3v3), null
   * for free-for-all. `winner` stays the seat that clinched the game.
   */
  winnerTeam: TeamId | null;
}

export class CheckersGame {
  readonly seats: readonly number[];
  /** hexKey → seat (owner camp). */
  readonly occupancy: Map<string, number> = new Map();
  /** seat → set of hexKeys holding its marbles. */
  private readonly marbleKeys: Map<number, Set<string>> = new Map();
  private turnIdx = 0;
  private finishedFlag = false;
  private winnerSeat: number | null = null;
  private winnerTeamId: TeamId | null = null;

  constructor(seats: readonly number[]) {
    if (seats.length < 2) throw new Error('CheckersGame requires ≥ 2 seats');
    this.seats = [...seats];
    for (const seat of this.seats) {
      const cells = CAMP_POSITIONS[seat];
      if (!cells || cells.length !== MARBLES_PER_CAMP) {
        throw new Error(`Camp ${seat} is malformed`);
      }
      const set = new Set<string>();
      for (const p of cells) {
        const k = hexKey(p);
        this.occupancy.set(k, seat);
        set.add(k);
      }
      this.marbleKeys.set(seat, set);
    }
  }

  /** Factory for the standard layouts (RULES.md §6). */
  static create(playerCount: PlayerCount): CheckersGame {
    const seats = SEAT_LAYOUTS[playerCount];
    if (!seats) throw new Error(`Unsupported player count: ${playerCount}`);
    return new CheckersGame(seats);
  }

  clone(): CheckersGame {
    const g = Object.create(CheckersGame.prototype) as CheckersGame;
    (g as unknown as { seats: number[] }).seats = [...this.seats];
    (g as unknown as { occupancy: Map<string, number> }).occupancy = new Map(this.occupancy);
    (g as unknown as { marbleKeys: Map<number, Set<string>> }).marbleKeys = new Map(
      [...this.marbleKeys].map(([s, set]) => [s, new Set(set)])
    );
    (g as unknown as { turnIdx: number }).turnIdx = this.turnIdx;
    (g as unknown as { finishedFlag: boolean }).finishedFlag = this.finishedFlag;
    (g as unknown as { winnerSeat: number | null }).winnerSeat = this.winnerSeat;
    (g as unknown as { winnerTeamId: TeamId | null }).winnerTeamId = this.winnerTeamId;
    return g;
  }

  // ── Queries ─────────────────────────────────────────────
  get currentPlayer(): number {
    return this.seats[this.turnIdx] ?? 0;
  }

  get turnIndex(): number {
    return this.turnIdx;
  }

  get finished(): boolean {
    return this.finishedFlag;
  }

  get winner(): number | null {
    return this.winnerSeat;
  }

  /** Winning team in team formats (RULES.md §11), null for FFA / unfinished. */
  get winnerTeam(): TeamId | null {
    return this.winnerTeamId;
  }

  /**
   * Whether a seat has all ten marbles home. In team formats such seats stay
   * on the board but are skipped in the rotation (RULES.md §11.3).
   */
  isComplete(seat: number): boolean {
    return this.marblesInDestination(seat) === MARBLES_PER_CAMP;
  }

  /** Team of a seat (by camp id), null in free-for-all formats. */
  teamOf(seat: number): TeamId | null {
    return teamOfSeatIndex(this.seats.length, this.seats.indexOf(seat));
  }

  positionOf(seat: number, key: string): HexPos | null {
    return this.marbleKeys.get(seat)?.has(key) ? parseHexKey(key) : null;
  }

  /** Iterate all marble positions of a seat. */
  marblesOf(seat: number): HexPos[] {
    const out: HexPos[] = [];
    for (const k of this.marbleKeys.get(seat) ?? []) {
      const [q, r] = k.split(',').map(Number);
      out.push({ q: q ?? 0, r: r ?? 0 });
    }
    return out;
  }

  isDestination(seat: number, pos: HexPos): boolean {
    return campOf(pos) === destinationCamp(seat);
  }

  marblesInDestination(seat: number): number {
    let n = 0;
    for (const p of this.marblesOf(seat)) if (this.isDestination(seat, p)) n++;
    return n;
  }

  /** Remaining distance-to-goal for a seat (lower = closer to winning). */
  progress(seat: number): number {
    const tip = CAMP_POSITIONS[destinationCamp(seat)]?.[0];
    if (!tip) return 0;
    let sum = 0;
    for (const p of this.marblesOf(seat)) sum += hexDistance(p, tip);
    return sum;
  }

  /** Immediate legal targets from a position (steps + single hops). */
  immediateTargets(from: HexPos): Target[] {
    const targets: Target[] = [];
    for (const d of HEX_DIRECTIONS) {
      const step: HexPos = { q: from.q + d.q, r: from.r + d.r };
      if (isOnBoard(step) && !this.occupancy.has(hexKey(step))) {
        targets.push({ to: step, type: 'step', path: [from, step] });
      }
      const mid: HexPos = { q: from.q + d.q, r: from.r + d.r };
      const over: HexPos = { q: from.q + 2 * d.q, r: from.r + 2 * d.r };
      if (isOnBoard(over) && this.occupancy.has(hexKey(mid)) && !this.occupancy.has(hexKey(over))) {
        targets.push({ to: over, type: 'hop', path: [from, over] });
      }
    }
    return targets;
  }

  /**
   * Every legal destination for the marble at `from` (RULES.md §4.4):
   * adjacent steps plus ALL reachable hop landings enumerated by BFS.
   * Any hop landing is a valid final destination — a chain may stop there
   * (§4.2) — and each target carries its full hop path for animation/apply.
   */
  reachableTargets(from: HexPos): Target[] {
    const targets: Target[] = [];
    for (const t of this.immediateTargets(from)) {
      if (t.type === 'step') targets.push(t);
    }
    // BFS over hop edges. `visited` seeds with the origin (no-revisit, §4.4)
    // and holds every landing already enumerated, which both prevents cycles
    // and guarantees each destination is reported once via a valid path.
    const visited = new Set<string>([hexKey(from)]);
    const queue: HexPos[][] = [[from]];
    while (queue.length > 0) {
      const path = queue.shift() as HexPos[];
      const cur = path[path.length - 1] as HexPos;
      for (const d of HEX_DIRECTIONS) {
        const mid: HexPos = { q: cur.q + d.q, r: cur.r + d.r };
        const over: HexPos = { q: cur.q + 2 * d.q, r: cur.r + 2 * d.r };
        const overKey = hexKey(over);
        if (
          isOnBoard(over) &&
          this.occupancy.has(hexKey(mid)) &&
          !this.occupancy.has(overKey) &&
          !visited.has(overKey)
        ) {
          visited.add(overKey);
          const next = [...path, over];
          targets.push({ to: over, type: 'hop', path: next });
          queue.push(next);
        }
      }
    }
    return targets;
  }

  /** Legal options for the current player. */
  legalTargets(): SeatTargets[] {
    const seat = this.currentPlayer;
    if (this.finishedFlag) return [];
    const out: SeatTargets[] = [];
    for (const p of this.marblesOf(seat)) {
      const targets = this.reachableTargets(p);
      if (targets.length > 0) out.push({ seat, marble: p, targets });
    }
    return out;
  }

  hasAnyMove(seat: number): boolean {
    if (this.finishedFlag) return false;
    for (const p of this.marblesOf(seat)) {
      if (this.immediateTargets(p).length > 0) return true;
    }
    return false;
  }

  // ── Mutations ───────────────────────────────────────────
  private setMarble(seat: number, from: HexPos, to: HexPos): void {
    const fromKey = hexKey(from);
    const toKey = hexKey(to);
    if (this.occupancy.get(fromKey) !== seat) throw new Error('Not your marble');
    if (this.occupancy.has(toKey)) throw new Error('Destination occupied');
    this.occupancy.delete(fromKey);
    this.occupancy.set(toKey, seat);
    const set = this.marbleKeys.get(seat);
    if (!set) throw new Error('Unknown seat');
    set.delete(fromKey);
    set.add(toKey);
  }

  /**
   * Execute one full turn action: a step or a hop chain of any length ≥ 1.
   * `to` may be ANY reachable landing (RULES.md §4.2 — the player chooses
   * where the chain stops); the marble travels the enumerated path and the
   * turn rotates. Returns the travelled path for animation/logging.
   */
  applyMove(from: HexPos, to: HexPos): MoveResult {
    if (this.finishedFlag) throw new Error('Game is finished');
    const seat = this.currentPlayer;
    if (this.occupancy.get(hexKey(from)) !== seat) throw new Error('Not your marble');

    const match = this.reachableTargets(from).find((t) => t.to.q === to.q && t.to.r === to.r);
    if (!match) throw new Error('Illegal move');

    this.setMarble(seat, from, match.to);

    // Win check (RULES.md §11): FFA ends on the first completion; team
    // formats end only when the mover's WHOLE team is home. A completed
    // member whose team is not done stays on the board and is skipped in
    // the rotation by advanceTurn().
    if (this.marblesInDestination(seat) === MARBLES_PER_CAMP) {
      const team = teamOfSeatIndex(this.seats.length, this.turnIdx);
      if (team === null || this.teamComplete(team)) {
        this.finishedFlag = true;
        this.winnerSeat = seat;
        this.winnerTeamId = team;
        return { path: match.path, finished: true, winner: seat, winnerTeam: team };
      }
    }

    this.advanceTurn();
    return { path: match.path, finished: false, winner: null, winnerTeam: null };
  }

  /** Skip current player (no legal moves) and advance the turn. */
  skipTurn(): void {
    this.advanceTurn();
  }

  /**
   * Advance to the next playable seat. Completed seats (all ten home) are
   * skipped in the rotation (RULES.md §11.3); completion is derived on
   * demand, so no extra state is persisted and clone()/restore() stay exact.
   */
  private advanceTurn(): void {
    const n = this.seats.length;
    this.turnIdx = (this.turnIdx + 1) % n;
    let guard = 0;
    while (
      guard < n &&
      this.marblesInDestination(this.seats[this.turnIdx] ?? -1) === MARBLES_PER_CAMP
    ) {
      this.turnIdx = (this.turnIdx + 1) % n;
      guard++;
    }
  }

  /** Every seat of the team has all ten marbles home. */
  private teamComplete(team: TeamId): boolean {
    for (const idx of teamSeatIndices(this.seats.length, team)) {
      const seat = this.seats[idx];
      if (seat === undefined || this.marblesInDestination(seat) !== MARBLES_PER_CAMP) return false;
    }
    return true;
  }

  /** Seat whose turn is next after the current one (mirrors advanceTurn). */
  nextSeat(): number {
    const n = this.seats.length;
    let idx = (this.turnIdx + 1) % n;
    let guard = 0;
    while (guard < n && this.marblesInDestination(this.seats[idx] ?? -1) === MARBLES_PER_CAMP) {
      idx = (idx + 1) % n;
      guard++;
    }
    return this.seats[idx] ?? 0;
  }

  // ── Wire snapshots (PROTOCOL.md §2 MatchState) ─────────
  /**
   * Serialize to the authoritative MatchState snapshot. Marbles are emitted
   * in deterministic (seat, q, r) order so server diffs/tests are stable.
   * `finished` carries ≤ 1 seat — the game ends on the first completion.
   */
  toMatchState(): MatchState {
    const marbles: { seat: number; pos: HexPos }[] = [];
    for (const seat of this.seats) {
      for (const pos of this.marblesOf(seat)) marbles.push({ seat, pos });
    }
    marbles.sort((a, b) => a.seat - b.seat || a.pos.q - b.pos.q || a.pos.r - b.pos.r);
    return {
      marbles,
      currentPlayer: this.currentPlayer,
      turnIndex: this.turnIdx,
      finished: this.winnerSeat !== null ? [this.winnerSeat] : [],
      winner: this.winnerSeat,
    };
  }

  /**
   * Rebuild a game from a MatchState snapshot (client resync, server tests).
   * The seat layout is derived from the marbles' seat set via SEAT_LAYOUTS,
   * so a snapshot is only valid for a complete, standard-layout game.
   * Throws on structurally invalid input — callers treat that as a protocol
   * bug (client) or an INTERNAL error (server), never as user input.
   */
  static restore(state: MatchState): CheckersGame {
    const seatSet = [...new Set(state.marbles.map((m) => m.seat))].sort((a, b) => a - b);
    const layout = SEAT_LAYOUTS[seatSet.length];
    if (!layout) throw new Error(`MatchState: unsupported seat count ${seatSet.length}`);
    const layoutSorted = [...layout].sort((a, b) => a - b);
    if (seatSet.join(',') !== layoutSorted.join(',')) {
      throw new Error(
        `MatchState: seats [${seatSet.join(',')}] don't fit layout ${seatSet.length}`
      );
    }

    const g = Object.create(CheckersGame.prototype) as CheckersGame;
    (g as unknown as { seats: number[] }).seats = [...layout];
    (g as unknown as { occupancy: Map<string, number> }).occupancy = new Map();
    const marbleKeys = new Map<number, Set<string>>();
    for (const s of layout) marbleKeys.set(s, new Set<string>());

    for (const { seat, pos } of state.marbles) {
      if (!isOnBoard(pos)) throw new Error(`MatchState: off-board marble ${hexKey(pos)}`);
      const k = hexKey(pos);
      if (g.occupancy.has(k)) throw new Error(`MatchState: duplicate marble at ${k}`);
      const set = marbleKeys.get(seat);
      if (!set) throw new Error(`MatchState: unknown seat ${seat}`);
      g.occupancy.set(k, seat);
      set.add(k);
    }
    for (const s of layout) {
      const set = marbleKeys.get(s);
      if (!set || set.size !== MARBLES_PER_CAMP) {
        throw new Error(`MatchState: seat ${s} has ${set?.size ?? 0} marbles`);
      }
    }
    (g as unknown as { marbleKeys: Map<number, Set<string>> }).marbleKeys = marbleKeys;

    const turnIdx = layout.indexOf(state.currentPlayer);
    if (turnIdx < 0) throw new Error(`MatchState: currentPlayer ${state.currentPlayer} not seated`);
    (g as unknown as { turnIdx: number }).turnIdx = turnIdx;

    const finished = state.winner !== null || state.finished.length > 0;
    (g as unknown as { finishedFlag: boolean }).finishedFlag = finished;
    const winnerSeat = state.winner ?? state.finished[0] ?? null;
    (g as unknown as { winnerSeat: number | null }).winnerSeat = winnerSeat;
    // Team is derived from the clinching seat — MatchState needs no new field.
    (g as unknown as { winnerTeamId: TeamId | null }).winnerTeamId =
      winnerSeat === null ? null : teamOfSeatIndex(layout.length, layout.indexOf(winnerSeat));
    return g;
  }
}
